import { Button, Flex, Text } from '@billy/design-system'
import React, { cloneElement, isValidElement, ReactElement, ReactNode, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TimelineStatusItem } from './elements/TimelineStatusItem'
import { TimelineStatusItemsList, TimelineStatusItemsListProps } from './elements/TimelineStatusItemsList'
import * as Styled from './styles'

export interface TimelineStatusProps {
  children?: ReactNode
  content?: ReactNode
  isCollapsed?: boolean
  isCollapsible?: boolean
  isLoading?: boolean
  maxHeight?: number
  minVisibleItemsCount?: number
  onCollapseChange?: () => void
  title?: ReactNode
}

export const TimelineStatus = ({
  children,
  content,
  isCollapsed: isCollapsedControlled = false,
  isCollapsible = true,
  isLoading,
  maxHeight,
  minVisibleItemsCount,
  onCollapseChange,
  title,
}: TimelineStatusProps): ReactElement => {
  const [isCollapsed, setIsCollapsed] = useState(isCollapsedControlled)

  const { t } = useTranslation()

  useEffect(() => {
    setIsCollapsed(isCollapsedControlled)
  }, [isCollapsedControlled])

  const toggle = useCallback(() => {
    setIsCollapsed((prevIsCollapsed) => !prevIsCollapsed)
    onCollapseChange?.()
  }, [onCollapseChange])

  const handleExpansionTriggerClick = useCallback(() => {
    toggle()
  }, [toggle])

  return (
    <Styled.TimelineStatusWrapper maxHeight={maxHeight}>
      <Styled.Header>
        {title &&
          (typeof title === 'string' ? (
            <Text variant="bodyLG" weight="medium">
              {title}
            </Text>
          ) : (
            title
          ))}
      </Styled.Header>
      {content && <Styled.ContentWrapper>{content}</Styled.ContentWrapper>}
      {children && isValidElement<TimelineStatusItemsListProps>(children) && (
        <Styled.ListWrapper>
          {cloneElement<TimelineStatusItemsListProps>(children, {
            isCollapsed: isCollapsible && isCollapsed,
            isLoading,
            minVisibleItemsCount,
          })}
        </Styled.ListWrapper>
      )}
      {isCollapsible && (
        <Flex>
          <Button
            variant="text"
            onClick={handleExpansionTriggerClick}
            iconRight={isCollapsed ? 'chevronDown' : 'chevronUp'}
          >
            {t(isCollapsed ? 'timeline_status.expander.view_more' : 'timeline_status.expander.view_less')}
          </Button>
        </Flex>
      )}
    </Styled.TimelineStatusWrapper>
  )
}

TimelineStatus.ItemsList = TimelineStatusItemsList
TimelineStatus.Item = TimelineStatusItem
